import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { MAX_PROVIDERS_LIMIT } from '../../constants/subscriptions';
import { DEFAULT_TEMPLATE_FILENAME } from '../../utils/csv/constants';
import { readAndParseCSV } from '../../utils/csv/parseRosterCSV';
import { downloadRosterTemplate } from '../../utils/csv/rosterTemplate';
import { validateRosterCSV } from '../../utils/csv/validateRosterCSV';
import { storeProvidersInCache } from '../../utils/serviceCalls/cache';
import { fetchFirstProviderByNpi } from '../../utils/serviceCalls/provider';
import { generateUniqueId } from '../../utils/utilities';
import { isDuplicateProvider, isValidNpi } from '../../utils/validation';
import { useAuthenticatedUser } from '../Auth';
import CheckoutSection from './CheckoutSection';
import CSVUploadModal from './CSVUpload/CSVUploadModal';
import CSVValidationResults from './CSVUpload/CSVValidationResults';
import { CSV_UPLOAD_MESSAGES } from './constants';
import HeroSection from './HeroSection';
import InstructionsSection from './InstructionsSection';
import ProviderRoster from './ProviderRoster';
import ProviderSearch from './ProviderSearch';

/**
 * @typedef {import('./types').RosterProvider} RosterProvider
 * @typedef {import('../../utils/serviceCalls/cache').SelectedProvider} SelectedProvider
 * @typedef {'idle'|'processing'|'navigating'|'error'} CheckoutStatus
 */

/**
 * Handles the checkout process for roster providers.
 * Stores providers in cache and navigates to checkout page.
 *
 * @param {RosterProvider[]} rosterProviders - List of providers to checkout
 * @param {string} userId - ID of the user making the purchase
 */
export const useProviderCheckout = (rosterProviders, userId) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState(/** @type {CheckoutStatus} */ ('idle'));
  const [checkoutError, setError] = useState('');

  const clearError = useCallback(() => {
    setError('');
    setStatus((s) => (s === 'error' ? 'idle' : s));
  }, []);

  const checkingOut = status === 'processing' || status === 'navigating';

  // Avoid state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Clear checkout errors when roster changes
  const prevRosterProvidersRef = useRef(rosterProviders);
  useEffect(() => {
    if (prevRosterProvidersRef.current !== rosterProviders) {
      prevRosterProvidersRef.current = rosterProviders;
      if (status === 'error') {
        clearError();
      }
    }
  }, [status, clearError, rosterProviders]);

  const checkout = useCallback(async () => {
    if (!Array.isArray(rosterProviders) || rosterProviders.length === 0) {
      setError('Please add at least one provider before continuing.');
      return false;
    }

    setStatus('processing');
    setError('');

    try {
      const cacheId = generateUniqueId();

      /** @type {SelectedProvider[]} */
      const selectedProviders = rosterProviders.map((p) => ({
        pwid: p.providerCode,
        last4: p.last4,
        email: p.email,
        providerName:
          p.displayName ||
          [p.firstName, p.lastName].filter(Boolean).join(' ') ||
          '',
      }));

      const response = await storeProvidersInCache(
        cacheId,
        userId,
        selectedProviders,
      );

      if (!response?.success) {
        throw new Error('Unable to proceed to checkout. Please try again.');
      }

      // Preserve session in URL so Back button returns to same session
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('session', cacheId);
          return next;
        },
        { replace: true },
      );

      // Navigate to checkout
      setStatus('navigating');
      navigate(`/checkout?session=${encodeURIComponent(cacheId)}`);

      return true;
    } catch (err) {
      console.error('Error during checkout:', err);
      const message = 'Unable to proceed to checkout. Please try again.';
      if (mountedRef.current) {
        setError(message);
        setStatus('error');
      }
      return false;
    }
  }, [userId, navigate, setSearchParams, rosterProviders]);

  return {
    checkout,
    checkoutError,
    checkingOut,
  };
};

/**
 * Page for building a roster of providers to sponsor with Elevated Profile subscriptions.
 *
 * User Flow:
 * 1. Search for provider by NPI or name/location
 * 2. Validate provider identity with last 4 digits of DEA/license
 * 3. Provide provider's work email address
 * 4. Add provider to roster
 * 5. Repeat for additional providers
 * 6. Continue to checkout to complete purchase
 */
const AddProvidersToRosterPage = () => {
  const { userId, fullName } = useAuthenticatedUser();

  const [rosterProviders, setRosterProviders] = useState(
    /** @type {RosterProvider[]} */ ([]),
  );

  // Placeholder for a useEffect that fetches existing roster providers on mount.
  // Existing roster providers from backend (providers already in user's roster)
  // This should be fetched from an API endpoint that returns current roster providers
  // Example: GET /api/elevatedroster/providers or similar endpoint
  // The response should include providerCode for each provider
  // This is used to prevent adding providers that are already in the roster
  const [existingRosterProviders /** setExistingRosterProviders */] = useState(
    /** @type {Array<{providerCode: string}>} */ ([]),
  );

  // CSV Upload Modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [csvValidationResults, setCSVValidationResults] = useState(
    /** @type {import('../../utils/csv/validateRosterCSV').CSVUploadResult | null} */ (
      null
    ),
  );
  const [isValidatingCSV, setIsValidatingCSV] = useState(false);
  const [csvUploadError, setCSVUploadError] = useState('');

  // Counter to force ProviderSearch remount when CSV results are set
  // This clears any NPI search errors when CSV upload shows errors
  const providerSearchKeyRef = useRef(0);
  const setCSVValidationResultsWithReset = useCallback((results) => {
    setCSVValidationResults(results);
    // Increment counter to force ProviderSearch remount and clear search errors
    if (results) {
      providerSearchKeyRef.current += 1;
    }
  }, []);

  const { checkout, checkoutError, checkingOut } = useProviderCheckout(
    rosterProviders,
    userId,
  );

  const handleAddProvider = useCallback(
    /** @param {RosterProvider} provider */
    (provider) => {
      if (!provider) return;
      setRosterProviders((prevProviders) => {
        if (isDuplicateProvider(provider.providerCode, prevProviders)) {
          return prevProviders;
        }

        return [...prevProviders, provider];
      });
    },
    [],
  );

  /**
   * Handles download template button click
   */
  const handleDownloadTemplate = useCallback(() => {
    try {
      downloadRosterTemplate(DEFAULT_TEMPLATE_FILENAME);
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  }, []);

  /**
   * Handles upload CSV button click
   * Clear previous validation results before opening modal
   */
  const handleUploadCSVClick = useCallback(() => {
    setCSVValidationResults(null);
    setCSVUploadError('');
    setIsUploadModalOpen(true);
  }, []);

  /**
   * Handles modal close - clear upload error and reset state
   */
  const handleModalClose = useCallback(() => {
    setIsUploadModalOpen(false);
    setCSVUploadError('');
    setIsValidatingCSV(false);
  }, []);

  /**
   * Handles file selection in modal - clear upload error
   */
  const handleFileSelect = useCallback(() => {
    setCSVUploadError('');
  }, []);

  /**
   * Handles CSV file upload
   * @param {File} file
   */
  const handleCSVUpload = useCallback(
    /** @param {File} file */
    async (file) => {
      setIsValidatingCSV(true);
      setCSVValidationResults(null);

      try {
        // Step 1: Parse CSV file
        const {
          rows,
          errors: parseErrors,
          headerError,
          rowCount,
          duplicateRowNumbers,
        } = await readAndParseCSV(file);

        if (headerError) {
          setCSVUploadError('Upload failed: Invalid headers');
          setIsValidatingCSV(false);
          return;
        }

        // Check if CSV exceeds maximum allowed rows (duplicates still count)
        // This check must happen BEFORE any async processing to prevent adding providers
        // rowCount is the total count of meaningful (non-empty) data rows found in the raw CSV
        if (rowCount > MAX_PROVIDERS_LIMIT) {
          const maxLimitError = {
            isValid: false,
            errors: [
              {
                rowNumber: 0,
                type: 'max_limit_exceeded',
                message: `The maximum number of providers allowed per upload is ${MAX_PROVIDERS_LIMIT}. Kindly re-upload the data ensuring it does not exceed this limit`,
              },
            ],
            provider: null,
            rowNumber: 0,
          };
          setCSVValidationResultsWithReset({
            results: [maxLimitError],
            totalRows: rowCount,
            validRows: 0,
            errorRows: 1,
          });
          setIsUploadModalOpen(false);
          setIsValidatingCSV(false);
          return;
        }

        // Check duplicates (2nd+ occurrences only) and show non-blocking warning banner
        let duplicateBannerResult = null;
        if (
          Array.isArray(duplicateRowNumbers) &&
          duplicateRowNumbers.length > 0
        ) {
          const sortedRows = Array.from(new Set(duplicateRowNumbers))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);

          const formatRowList = (nums) => {
            if (nums.length === 0) return '';
            if (nums.length === 1) return String(nums[0]);
            if (nums.length === 2) return `${nums[0]} and ${nums[1]}`;
            return `${nums.slice(0, -1).join(', ')} and ${nums[nums.length - 1]}`;
          };

          const duplicateMessage =
            sortedRows.length > 10
              ? CSV_UPLOAD_MESSAGES.DUPLICATE_PROVIDERS_OVER_10
              : CSV_UPLOAD_MESSAGES.duplicateProvidersWithRows(
                  formatRowList(sortedRows),
                );

          // Non-blocking banner: we still validate and add valid providers, but show this message.
          duplicateBannerResult = {
            isValid: false,
            errors: [
              {
                rowNumber: 0,
                type: 'duplicate_providers',
                message: duplicateMessage,
              },
            ],
            provider: null,
            rowNumber: 0,
          };
        }

        if (rows.length === 0 && parseErrors.length === 0) {
          setIsUploadModalOpen(false);
          setIsValidatingCSV(false);
          return;
        }

        // Convert parsing errors to validation result format
        // If NPI is valid, fetch provider data to show full details
        const parseErrorResults = await Promise.all(
          parseErrors.map(async (error) => {
            let providerName = '';
            if (error.firstName && error.lastName) {
              providerName = `${error.firstName} ${error.lastName}`.trim();
            } else if (error.firstName) {
              providerName = error.firstName.trim();
            } else if (error.lastName) {
              providerName = error.lastName.trim();
            }

            let provider = providerName
              ? {
                  displayName: providerName,
                }
              : null;

            // If NPI is present and valid, fetch provider data to show full details
            if (error.npi && isValidNpi(error.npi.trim())) {
              try {
                const trimmedNpi = error.npi.trim();
                const { provider: fetchedProvider } =
                  await fetchFirstProviderByNpi(trimmedNpi);
                if (fetchedProvider) {
                  // Use fetched provider data (includes image, specialty, address, etc.)
                  provider = fetchedProvider;
                }
              } catch (fetchError) {
                // If fetch fails, fall back to CSV name
                console.error(
                  'Error fetching provider for parse error:',
                  fetchError,
                );
              }
            }

            return {
              isValid: false,
              errors: [
                {
                  rowNumber: error.row,
                  type: 'parse_error',
                  message: error.message,
                },
              ],
              provider,
              rowNumber: error.row,
            };
          }),
        );

        if (rows.length === 0 && parseErrors.length > 0) {
          setCSVValidationResultsWithReset({
            results: duplicateBannerResult
              ? [duplicateBannerResult, ...parseErrorResults]
              : parseErrorResults,
            totalRows: rowCount,
            validRows: 0,
            errorRows: parseErrors.length + (duplicateBannerResult ? 1 : 0),
          });
          setIsUploadModalOpen(false);
          setIsValidatingCSV(false);
          return;
        }

        // Step 2: Validate all successfully parsed rows
        const validationResult = await validateRosterCSV(
          rows,
          rosterProviders,
          existingRosterProviders,
        );

        // Combine only INVALID results for error display
        // Valid results should NOT appear in error section - they go to roster only
        const invalidValidationResults = validationResult.results.filter(
          (r) => !r.isValid,
        );
        const combinedResults = [
          ...(duplicateBannerResult ? [duplicateBannerResult] : []),
          ...parseErrorResults,
          ...invalidValidationResults,
        ];

        // Step 3: Separate valid and invalid data (only from validation, not parse errors)
        const validProviders = validationResult.results
          .filter(
            (r) =>
              r.isValid === true &&
              r.provider &&
              r.provider.providerCode && // Must have providerCode
              (!r.errors || r.errors.length === 0),
          )
          .map((r) => r.provider);

        const errorProviderCodes = new Set(
          combinedResults
            .filter((r) => r.provider?.providerCode)
            .map((r) => r.provider.providerCode),
        );

        // Track NPIs from parse errors (parse errors that don't have providerCode)
        // This is more reliable than matching on displayName since multiple providers can have the same name
        const errorProviderNpis = new Set(
          parseErrors.map((error) => error.npi?.trim()).filter(Boolean),
        );

        const validProviderCodes = new Set(
          validProviders.map((p) => p.providerCode).filter(Boolean),
        );
        const providersWithAnyErrors = new Set(
          Array.from(validProviderCodes).filter((code) =>
            combinedResults.some((r) => r.provider?.providerCode === code),
          ),
        );

        // Step 4: Add valid providers to roster (batch update to avoid stale state issues)
        // Double-check: Don't add any provider that has errors
        if (validProviders.length > 0) {
          setRosterProviders((prevProviders) => {
            const newProviders = [];
            const existingProviderCodes = new Set(
              prevProviders.map((p) => p.providerCode).filter(Boolean),
            );

            validProviders.forEach((provider) => {
              // add provider if it passes ALL checks
              // 1. Has providerCode
              // 2. Not already in roster
              // 3. Not in errorProviderCodes (validation errors)
              // 4. Not in providersWithAnyErrors (duplicate rows with errors)
              // 5. NPI doesn't match any parse error (parse errors don't have providerCode)
              // Use NPI matching instead of displayName to avoid excluding valid providers with common names
              const providerNpiMatch = provider.npi
                ? errorProviderNpis.has(provider.npi.trim())
                : false;

              if (
                provider?.providerCode &&
                !existingProviderCodes.has(provider.providerCode) &&
                !errorProviderCodes.has(provider.providerCode) &&
                !providersWithAnyErrors.has(provider.providerCode) &&
                !providerNpiMatch // Don't add if NPI matches a parse error
              ) {
                existingProviderCodes.add(provider.providerCode);
                newProviders.push(provider);
              }
            });

            // Only update if we have new providers to add
            if (newProviders.length > 0) {
              return [...prevProviders, ...newProviders];
            }
            return prevProviders;
          });
        }

        // Step 5: Store ONLY invalid results for error display

        const totalRows = rowCount;
        const validRows = validationResult.validRows;
        const errorRows =
          parseErrors.length +
          invalidValidationResults.length +
          (duplicateBannerResult ? 1 : 0);

        // Only set validation results if there are errors to display
        if (combinedResults.length > 0) {
          setCSVValidationResultsWithReset({
            results: combinedResults,
            totalRows,
            validRows,
            errorRows,
          });
        } else {
          // No errors - clear any previous error results
          setCSVValidationResults(null);
        }

        // Close modal
        setIsUploadModalOpen(false);
      } catch (error) {
        console.error('CSV upload error:', error);
        // Show error message
        setCSVValidationResultsWithReset({
          results: [
            {
              isValid: false,
              errors: [
                {
                  rowNumber: 0,
                  type: 'upload_error',
                  message:
                    error.message ||
                    'Failed to process CSV file. Please try again.',
                },
              ],
              provider: null,
              rowNumber: 0,
            },
          ],
          totalRows: 0,
          validRows: 0,
          errorRows: 1,
        });
      } finally {
        setIsValidatingCSV(false);
      }
    },
    [
      rosterProviders,
      existingRosterProviders,
      setCSVValidationResultsWithReset,
    ],
  );

  return (
    <div className="w-full bg-gray-20 font-hg-sans text-gray-90">
      <HeroSection adminName={fullName} />

      <section className="relative px-0 mt-0 md:px-25 md:-mt-[264px]">
        <div className="mx-auto w-full bg-white z-10 px-4 py-7 md:px-7 md:py-10 md:rounded-xl md:shadow-lg">
          <InstructionsSection
            onDownloadTemplate={handleDownloadTemplate}
            onUploadCSV={handleUploadCSVClick}
          />

          <div data-testid="provider-search-section">
            <ProviderSearch
              key={providerSearchKeyRef.current}
              onAdd={handleAddProvider}
              disabled={checkingOut}
              existingProviders={rosterProviders}
              existingRosterProviders={existingRosterProviders}
              onSearchFieldsChange={() => {
                // Clear CSV validation results when user starts searching
                if (csvValidationResults) {
                  setCSVValidationResults(null);
                }
              }}
            />
          </div>

          {/* CSV Validation Results */}
          {csvValidationResults && (
            <CSVValidationResults validationResults={csvValidationResults} />
          )}

          <div data-testid="provider-roster-section" className="pb-15 md:pb-10">
            <ProviderRoster
              providers={rosterProviders}
              onProvidersChange={setRosterProviders}
              disabled={checkingOut}
            />
          </div>
        </div>
      </section>
      <section className="w-full h-25 relative" />
      {/* Sticky checkout section - full width outside container */}
      <div
        data-testid="checkout-section"
        className="sticky bottom-0 w-full bg-white border-t border-gray-30 shadow-lg z-50"
      >
        <div className="max-w-7xl ml-auto mr-0 px-4 py-4 md:px-7">
          <CheckoutSection
            providers={rosterProviders}
            checkingOut={checkingOut}
            checkoutError={checkoutError}
            onCheckout={checkout}
          />
        </div>
      </div>

      {/* CSV Upload Modal */}
      <CSVUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleModalClose}
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleCSVUpload}
        isValidating={isValidatingCSV}
        uploadError={csvUploadError}
        onFileSelect={handleFileSelect}
      />
    </div>
  );
};

export default AddProvidersToRosterPage;
